class ContratoDetalhamento {
    static get $inject() {
        return ["$scope", "$filter", "$element", "$stateParams", "gql", "Faturamento"]
    }

    constructor(scope, filter, element, stateParams, gql, Faturamento) {
        this.applyAsync = fn => scope.$applyAsync(fn)
        this.watch = (...args) => scope.$watch(...args)
        this.filter = filter
        this.element = element
        this.gql = gql
        this.Faturamento = Faturamento

        this.contratoId = parseInt(stateParams.contratoId)
        this.filterEmpresas = this.filterEmpresas.bind(this)
        this.filterFiliais = this.filterFiliais.bind(this)
    }

    $onInit() {
        this.initialPromise = Promise
            .all([this.loadProdutos(), this.loadContrato()])
            .then(([produtosDisponiveis, { contrato: { id, cliente, empresas } }]) => {
                this.produtos = produtosDisponiveis

                const permissoes = empresas.flatMap(empresa => empresa.permissoes)
                const usuarios = permissoes.map(permissao => permissao.usuario).uniqBy(u => u.id)
                const filiais = empresas.flatMap(empresa => empresa.filiais)

                this.empresasUsuariosMap = this.buildEmpresasUsuariosMap(empresas)
                this.usuariosGroupedByFilial = this.groupUsuariosByFilial(permissoes)
                this.filiaisProdutosMap = this.buildFiliaisProdutosMap(filiais)
                this.empresasProdutosCountMap = this.buildEmpresasProdutosMap(empresas)

                const produtos = this.buildContratoProdutos(empresas)
                empresas = this.buildEmpresasList(empresas)

                this.contrato = {
                    id,
                    cliente,
                    produtos,
                    usuarios,
                    empresas,
                    empresasCount: empresas.length,
                    filiaisCount: filiais.length,
                    usuariosCount: usuarios.length,
                    nfesItensCount: empresas.reduce((acc, { nfesItensCount }) => acc + nfesItensCount, 0)
                }
            })
            .finally(() => this.applyAsync())
            .then(() => this.setupWatchers())
    }

    buildEmpresasUsuariosMap(empresas) {
        return new Map(empresas.map(empresa => {
            return [empresa.id, empresa.permissoes.map(permissao => permissao.usuario)]
        }))
    }

    groupUsuariosByFilial(permissoes) {
        return permissoes
            .flatMap(p => p.filiais.map(f => ({ filial: f, permissao: p })))
            .groupBy(({ filial }) => filial.id, ({ permissao }) => permissao.usuario)
    }

    buildFiliaisProdutosMap(filiais) {
        const filiaisProdutos = filiais
            .map(({ id, produtosContratados }) => {
                const produtos = produtosContratados
                    ? this.produtos.filter(p => produtosContratados.produtos.includes(p.id))
                    : []

                return [id, produtos]
            })

        return new Map(filiaisProdutos)
    }

    buildEmpresasProdutosMap(empresas) {
        const empresasProdutos = empresas
            .map(empresa => {
                const produtos = empresa.filiais
                    .flatMap(filial => this.filiaisProdutosMap.get(filial.id))
                    .groupBy(produto => produto.nome, produto => produto)

                return [
                    empresa.id,
                    Object.entries(produtos).map(([nome, produtos]) => {
                        return { nome, quantidade: produtos.length }
                    })
                ]
            })

        return new Map(empresasProdutos)
    }

    buildContratoProdutos(empresas) {
        const empresaProdutosCountGroupedByNome = empresas
            .flatMap(empresa => this.empresasProdutosCountMap.get(empresa.id))
            .groupBy(produto => produto.nome, produto => produto.quantidade)

        return Object.entries(empresaProdutosCountGroupedByNome)
            .map(([nome, quantidades]) => {
                return {
                    nome,
                    quantidade: quantidades.reduce((acc, qtd) => acc + qtd, 0)
                }
            })
    }

    buildEmpresasList(empresas) {
        return empresas
            .map((empresa) => {
                const { id, filiais } = empresa

                return {
                    ...empresa,
                    nfesItensCount: filiais.reduce((acc, { nfesItensCount }) => acc + nfesItensCount, 0),
                    produtos: this.empresasProdutosCountMap.get(id),
                    usuarios: this.empresasUsuariosMap.get(id),
                    usuariosCount: this.empresasUsuariosMap.get(id).length,
                    filiaisCount: filiais.length,
                    filiais: filiais
                        .map(filial => {
                            return {
                                ...filial,
                                produtos: this.filiaisProdutosMap.get(filial.id),
                                usuarios: this.usuariosGroupedByFilial[filial.id]
                            }
                        })
                        .sortBy(filial => filial.cnpj)
                }
            })
            .sortBy(empresa => empresa.razaoSocial)
    }

    setupWatchers() {
        this.watch(() => this.produtoForFilterEmpresas, this.filterEmpresas)
        this.watch(() => this.usuarioForFilterEmpresas, this.filterEmpresas)
        this.watch(() => this.empresaSearch, this.filterEmpresas)
        this.watch(() => this.produtoForFilterFiliais, this.filterFiliais)
        this.watch(() => this.usuarioForFilterFiliais, this.filterFiliais)
        this.watch(() => this.filialSearch, this.filterFiliais)
        this.watch(() => this.empresa, this.filterFiliais)
    }

    filterEmpresas() {
        let empresas = this.contrato.empresas

        if (this.produtoForFilterEmpresas) {
            empresas = empresas.filter(empresa => {
                return empresa.produtos.some(produto =>
                    produto.nome == this.produtoForFilterEmpresas.nome
                )
            })
        }

        if (this.usuarioForFilterEmpresas) {
            empresas = empresas.filter(empresa => {
                return empresa.usuarios.some(usuario =>
                    usuario.id == this.usuarioForFilterEmpresas.id
                )
            })
        }

        this.filteredEmpresas = this.filter("filter")(empresas, this.empresaSearch)
    }

    filterFiliais() {
        if (!this.empresa) return []

        let filiais = this.empresa.filiais

        if (this.produtoForFilterFiliais) {
            filiais = filiais.filter(filial => {
                return filial.produtos.some(produto =>
                    produto.nome == this.produtoForFilterFiliais.nome
                )
            })
        }

        if (this.usuarioForFilterFiliais) {
            filiais = filiais.filter(filial => {
                return filial.usuarios.some(usuario =>
                    usuario.id == this.usuarioForFilterFiliais.id
                )
            })
        }

        this.filteredFiliais = this.filter("filter")(filiais, this.filialSearch)
    }

    selectProdutoForFilterEmpresas(produto) {
        if (this.produtoForFilterEmpresas && this.produtoForFilterEmpresas.nome == produto.nome) {
            this.produtoForFilterEmpresas = null
        } else {
            this.produtoForFilterEmpresas = produto
            this.isEmpresasCollapsed = false
            this.empresa = null
            this.scrollToEmpresas()
        }
    }

    selectProdutoForFilterFiliais(produto) {
        if (this.produtoForFilterFiliais && this.produtoForFilterFiliais.nome == produto.nome) {
            this.produtoForFilterFiliais = null
        } else {
            this.produtoForFilterFiliais = produto
            this.isFiliaisCollapsed = false
            this.filial = null
            this.scrollToFiliais()
        }
    }

    selectUsuarioForFilterEmpresas(usuario) {
        if (this.usuarioForFilterEmpresas && this.usuarioForFilterEmpresas.id == usuario.id) {
            this.usuarioForFilterEmpresas = null
        } else {
            this.usuarioForFilterEmpresas = usuario
            this.isEmpresasCollapsed = false
            this.empresa = null
            this.scrollToEmpresas()
        }
    }

    selectUsuarioForFilterFiliais(usuario) {
        if (this.usuarioForFilterFiliais && this.usuarioForFilterFiliais.id == usuario.id) {
            this.usuarioForFilterFiliais = null
        } else {
            this.usuarioForFilterFiliais = usuario
            this.isFiliaisCollapsed = false
            this.filial = null
            this.scrollToFiliais()
        }
    }

    selectEmpresa(empresa) {
        this.empresa = empresa
        this.filial = null
        this.produtoForFilterFiliais = null
        this.usuarioForFilterFiliais = null
    }

    selectFilial(filial) {
        this.filial = filial
    }

    scrollToEmpresas() {
        this.scrollIntoView(`contrato-empresas-list`)
    }

    scrollToFiliais() {
        this.scrollIntoView(`empresa-filiais-list`)
    }

    scrollIntoView(name) {
        this.element.context.querySelector(`[name=${name}]`).scrollIntoView({ behavior: 'smooth' })
    }

    loadProdutos() {
        return this.Faturamento.listProdutos()
    }

    loadContrato() {
        const query = gql`
            query Contrato($id: String!) {
                contrato(id: $id) {
                    id
                    cliente {
                        id
                        nome
                        cpfCnpj
                    }
                    empresas {
                        id
                        raizCnpj
                        razaoSocial
                        permissoes {
                            id
                            usuario {
                                id
                                nome
                            }
                            empresa {
                                id
                            }
                            filiais {
                                id
                            }
                        }
                        filiais(ativa: true) {
                            id
                            cnpj
                            razaoSocial
                            nfesItensCount
                            empresa {
                                id
                            }
                            produtosContratados {
                                id
                                produtos
                            }
                            cidade {
                                id
                                nome
                                estado {
                                    id
                                    sigla
                                }
                            }
                        }
                    }
                }
            }
        `

        return this.gql.query(query, { id: this.contratoId })
    }

    buildCacheKey(text) {
        return `contrato-detalhamento-${this.contrato.id}-${text}`
    }
}

angular.module("eficienciaFiscalModule")
    .component("contratoDetalhamento", {
        controller: ContratoDetalhamento,
        template: `
            <loading promise="$ctrl.initialPromise">
                <div ng-if="$ctrl.contrato.id">
                    <div style="margin: 10px 0px 0px 20px;">
                        <a class="breadcrumb-link" href="/contratos/">Contratos</a> / 
                        <a class="breadcrumb-link" ng-href="/contratos/{{ $ctrl.contrato.id }}">
                            {{ $ctrl.contrato.cliente.cpfCnpj | cpfCnpj }}
                        </a> / 
                        Detalhamento
                    </div>

                    <h2 style="margin: 20px">
                        <i class="fa fa-credit-card"></i>
                        <span>
                            Detalhamento de utilização do contrato 
                            <b>{{ $ctrl.contrato.cliente.nome }}</b> 
                            nos últimos 30 dias
                        </span>
                    </h2>

                    <hr class="no-margin" />

                    <div class="grid-pane-4">
                        <div class="pane pane-top">
                            <span class="title">Total de empresas (Raiz)</span>
                            <span class="value">{{ $ctrl.contrato.empresasCount }}</span>
                        </div>

                        <div class="pane pane-top">
                            <span class="title">Total de filiais (CNPJ)</span>
                            <span class="value">{{ $ctrl.contrato.filiaisCount }}</span>
                        </div>

                        <div class="pane pane-top">
                            <span class="title">Total de usuários únicos</span>
                            <span class="value">{{ $ctrl.contrato.usuariosCount }}</span>
                        </div>

                        <div class="pane pane-top">
                            <span class="title">Total de itens movimentados: </span>
                            <span class="value">{{ $ctrl.contrato.nfesItensCount }}</span>
                        </div>
                    </div>

                    <div class="grid-pane-3">
                        <div class="pane pane-bottom">
                            <ef-collapse class="pane-content" cache-as="{{ $ctrl.buildCacheKey('produtos') }}">
                                <ef-collapse-header>
                                    <h5>Produtos contratados</h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div ng-if="!$ctrl.contrato.produtos.length" class="text-muted">
                                        Nenhum produto contratado.
                                    </div>

                                    <table
                                        ng-if="$ctrl.contrato.produtos.length"
                                        class="table table-hover table-condensed data-table">
                                        <tr ng-repeat="produto in $ctrl.contrato.produtos">
                                            <td>{{ produto.nome }}</td>
                                            <td class="text-right">{{ produto.quantidade }}</td>
                                            <td width="1">
                                                <span
                                                    class="btn-filter"
                                                    ng-class="{'produto-selected': $ctrl.produtoForFilterEmpresas.nome == produto.nome}"
                                                    tooltip="Filtrar as empresas em que este produto é utilizado.">
                                                    <i class="fa fa-crosshairs" ng-click="$ctrl.selectProdutoForFilterEmpresas(produto)"></i>
                                                </span>
                                            </td>
                                        </tr>
                                    </table>
                                </ef-collapse-content>
                            </ef-collapse>

                            <ef-collapse class="pane-content" cache-as="{{ $ctrl.buildCacheKey('usuarios') }}">
                                <ef-collapse-header>
                                    <h5>Usuários no contrato</h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div ng-if="!$ctrl.contrato.usuarios.length" class="text-muted">
                                        Nenhum usuário vinculado.
                                    </div>

                                    <table class="table table-hover table-condensed data-table">
                                        <tr ng-repeat="usuario in $ctrl.contrato.usuarios">
                                            <td>{{ $index + 1 }}) {{ usuario.nome }}</td>
                                            <td width="1">
                                                <span
                                                    class="btn-filter"
                                                    ng-class="{'usuario-selected': $ctrl.usuarioForFilterEmpresas.id == usuario.id}"
                                                    tooltip="Filtrar as empresas em que este usuário está vinculado.">
                                                    <i class="fa fa-crosshairs" ng-click="$ctrl.selectUsuarioForFilterEmpresas(usuario)"></i>
                                                </span>
                                            </td>
                                        </tr>
                                    </table>
                                </ef-collapse-content>
                            </ef-collapse>

                            <ef-collapse
                                class="pane-content"
                                is-collapsed="$ctrl.isEmpresasCollapsed"
                                cache-as="{{ $ctrl.buildCacheKey('empresas') }}">
                                <ef-collapse-header>
                                    <h5 name="contrato-empresas-list" class="pane-header">
                                        <span>Empresas no contrato</span>
                                        <span class="secondary" ng-hide="$ctrl.isEmpresasCollapsed">
                                            Exibindo {{ $ctrl.filteredEmpresas.length }} de {{ $ctrl.contrato.empresasCount }}
                                        </span>
                                    </h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div>
                                        <ef-search-input ng-model="$ctrl.empresaSearch"></ef-search-input>

                                        <div class="tags" ng-if="$ctrl.produtoForFilterEmpresas || $ctrl.usuarioForFilterEmpresas">
                                            <span
                                                ng-if="$ctrl.produtoForFilterEmpresas"
                                                class="tag tag-produto">
                                                <span>{{ $ctrl.produtoForFilterEmpresas.nome }}</span>
                                                <i class="fa fa-times icon" ng-click="$ctrl.produtoForFilterEmpresas = null"></i>
                                            </span>

                                            <span
                                                ng-if="$ctrl.usuarioForFilterEmpresas"
                                                class="tag tag-usuario">
                                                <span>{{ $ctrl.usuarioForFilterEmpresas.nome }}</span>
                                                <i class="fa fa-times icon" ng-click="$ctrl.usuarioForFilterEmpresas = null"></i>
                                            </span>
                                        </div>
                                    </div>

                                    <div ng-if="!$ctrl.filteredEmpresas.length" class="text-muted" style="margin-top: 10px">
                                        Nenhuma empresa atende ao filtro utilizado.
                                    </div>

                                    <div ng-if="$ctrl.filteredEmpresas.length" class="data-list">
                                        <div 
                                            ng-repeat="empresa in $ctrl.filteredEmpresas" 
                                            ng-click="$ctrl.selectEmpresa(empresa)"
                                            ng-class="{'data-list-item-selected': $ctrl.empresa.id == empresa.id}"
                                            class="data-list-item"
                                            title="{{ empresa.razaoSocial }}">

                                            <div class="primary-row">
                                                <span class="overflow">
                                                    {{ $index + 1 }}) {{ empresa.razaoSocial }}
                                                </span>

                                                <span class="secondary-text">
                                                    {{ empresa.raizCnpj | cpfCnpj }}
                                                </span>
                                            </div>

                                            <div class="secondary-row">
                                                <span>
                                                    Usuários: <b>{{ empresa.usuariosCount }}</b>
                                                </span>

                                                <span>
                                                    Filiais: <b>{{ empresa.filiaisCount }}</b>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </ef-collapse-content>
                            </ef-collapse>
                        </div>

                        <div class="pane pane-message" ng-if="!$ctrl.empresa">
                            <span>
                                Selecione uma empresa na lista à esquerda para ver mais detalhes.
                            </span>
                        </div>

                        <div class="pane pane-bottom" ng-if="$ctrl.empresa">
                            <div class="pane-content pane-content-inline">
                                <span class="title">Total de itens da empresa: </span>
                                <span class="value text-right">{{ $ctrl.empresa.nfesItensCount }}</span>
                            </div>

                            <ef-collapse class="pane-content" cache-as="{{ $ctrl.buildCacheKey('empresa-produtos') }}">
                                <ef-collapse-header>
                                    <h5>Produtos contratados na empresa</h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div ng-if="!$ctrl.empresa.produtos.length" class="text-muted">
                                        Nenhum produto contratado.
                                    </div>

                                    <table
                                        ng-if="$ctrl.empresa.produtos.length"
                                        class="table table-hover table-condensed data-table">
                                        <tr ng-repeat="produto in $ctrl.empresa.produtos">
                                            <td>{{ produto.nome }}</td>
                                            <td class="text-right">{{ produto.quantidade }}</td>
                                            <td width="1">
                                                <span
                                                    class="btn-filter"
                                                    ng-class="{'produto-selected': $ctrl.produtoForFilterFiliais.nome == produto.nome}"
                                                    tooltip="Filtrar as filiais em que este produto é utilizado.">
                                                    <i class="fa fa-crosshairs" ng-click="$ctrl.selectProdutoForFilterFiliais(produto)"></i>
                                                </span>
                                            </td>
                                        </tr>
                                    </table>
                                </ef-collapse-content>
                            </ef-collapse>

                            <ef-collapse class="pane-content" cache-as="{{ $ctrl.buildCacheKey('empresa-usuarios') }}">
                                <ef-collapse-header>
                                    <h5>Usuários da empresa</h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div ng-if="!$ctrl.empresa.usuarios.length" class="text-muted">
                                        Nenhum usuário vinculado.
                                    </div>

                                    <table class="table table-hover table-condensed data-table">
                                        <tr ng-repeat="usuario in $ctrl.empresa.usuarios">
                                            <td>{{ $index + 1 }}) {{ usuario.nome }}</td>
                                            <td width="1">
                                                <span
                                                    class="btn-filter"
                                                    ng-class="{'usuario-selected': $ctrl.usuarioForFilterFiliais.id == usuario.id}"
                                                    tooltip="Filtrar as filiais em que este usuário está vinculado.">
                                                    <i class="fa fa-crosshairs" ng-click="$ctrl.selectUsuarioForFilterFiliais(usuario)"></i>
                                                </span>
                                            </td>
                                        </tr>
                                    </table>
                                </ef-collapse-content>
                            </ef-collapse>

                            <ef-collapse
                                class="pane-content"
                                is-collapsed="$ctrl.isFiliaisCollapsed"
                                cache-as="{{ $ctrl.buildCacheKey('empresa-filiais') }}">
                                <ef-collapse-header>
                                    <h5 name="empresa-filiais-list" class="pane-header">
                                        <span>Filiais da empresa</span>
                                        <span class="secondary" ng-hide="$ctrl.isFiliaisCollapsed">
                                            Exibindo {{ $ctrl.filteredFiliais.length }} de {{ $ctrl.empresa.filiaisCount }}
                                        </span>
                                    </h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div>
                                        <ef-search-input ng-model="$ctrl.filialSearch"></ef-search-input>

                                        <div class="tags" ng-if="$ctrl.produtoForFilterFiliais || $ctrl.usuarioForFilterFiliais">
                                            <span
                                                ng-if="$ctrl.produtoForFilterFiliais"
                                                class="tag tag-produto">
                                                <span>{{ $ctrl.produtoForFilterFiliais.nome }}</span>
                                                <i class="fa fa-times icon" ng-click="$ctrl.produtoForFilterFiliais = null"></i>
                                            </span>

                                            <span
                                                ng-if="$ctrl.usuarioForFilterFiliais"
                                                class="tag tag-usuario">
                                                <span>{{ $ctrl.usuarioForFilterFiliais.nome }}</span>
                                                <i class="fa fa-times icon" ng-click="$ctrl.usuarioForFilterFiliais = null"></i>
                                            </span>
                                        </div>
                                    </div>

                                    <div ng-if="!$ctrl.filteredFiliais.length" class="text-muted" style="margin-top: 10px">
                                        Nenhuma filial atende ao filtro utilizado.
                                    </div>

                                    <div ng-if="$ctrl.filteredFiliais.length" class="data-list">
                                        <div 
                                            ng-repeat="filial in $ctrl.filteredFiliais" 
                                            ng-click="$ctrl.selectFilial(filial)"
                                            ng-class="{'data-list-item-selected': $ctrl.filial.id == filial.id}"
                                            class="data-list-item"
                                            title="{{ filial.razaoSocial }}">

                                            <span class="overflow">
                                                {{ $index + 1 }}) 
                                                {{ (filial.cnpj | cpfCnpj).slice(11) }} |
                                                {{ filial.cidade.nome }}/{{ filial.cidade.estado.sigla }}
                                            </span>
                                        </div>
                                    </div>
                                </ef-collapse-content>
                            </ef-collapse>
                        </div>

                        <div class="pane pane-message" ng-if="$ctrl.empresa && !$ctrl.filial">
                            <span>
                                Selecione uma filial na lista à esquerda para ver mais detalhes.
                            </span>
                        </div>

                        <div class="pane pane-bottom" ng-if="$ctrl.empresa && $ctrl.filial">
                            <div class="pane-content pane-content-inline">
                                <span class="title">Total de itens da filial: </span>
                                <span class="value text-right">{{ $ctrl.filial.nfesItensCount }}</span>
                            </div>

                            <ef-collapse class="pane-content" cache-as="{{ $ctrl.buildCacheKey('empresa-filial-produtos') }}">
                                <ef-collapse-header>
                                    <h5>Produtos contratados para a filial</h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div ng-if="!$ctrl.filial.produtos.length" class="text-muted">
                                        Nenhum produto contratado.
                                    </div>

                                    <table
                                        ng-if="$ctrl.filial.produtos.length"
                                        class="table table-hover table-condensed data-table">
                                        <tr ng-repeat="produto in $ctrl.filial.produtos">
                                            <td>{{ produto.nome }}</td>
                                            <td class="text-right">{{ produto.quantidade }}</td>
                                        </tr>
                                    </table>
                                </ef-collapse-content>
                            </ef-collapse>

                            <ef-collapse class="pane-content" cache-as="{{ $ctrl.buildCacheKey('empresa-filial-usuarios') }}">
                                <ef-collapse-header>
                                    <h5>Usuários da filial</h5>
                                </ef-collapse-header>

                                <ef-collapse-content>
                                    <div ng-if="!$ctrl.filial.usuarios.length" class="text-muted">
                                        Nenhum usuário vinculado.
                                    </div>

                                    <table class="table table-hover table-condensed data-table">
                                        <tr ng-repeat="usuario in $ctrl.filial.usuarios">
                                            <td>{{ $index + 1 }}) {{ usuario.nome }}</td>
                                        </tr>
                                    </table>
                                </ef-collapse-content>
                            </ef-collapse>
                        </div>
                    </div>
                </div>
            </loading>
        `
    })
